import { useState, useRef, useEffect, useCallback, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { MemberPicker } from './NotificationPopup';
import { useGroupChat } from '../hooks/useGroupChat';
import { authHeaders } from '../services/auth';
import './Panels.css';

const WORKER_URL = process.env.REACT_APP_WORKER_URL || '';
// Chiave storica pre-SA-PRODUCTDB-SHARED-01: il database prodotti viveva solo nel
// localStorage del browser (mai in KV). Ora il database è condiviso (vedi useProductsDb
// in useStorage.js); questa chiave resta SOLO per offrire una migrazione una-tantum dei
// dati locali eventualmente ancora presenti su un browser che l'aveva usata prima.
const LEGACY_PRODUCT_DB_KEY = 'sirus_products_database_v1';

const normalizeDbProduct = (item = {}) => ({
  id: item.id || Date.now(),
  code: item.code || '',
  name: item.name || '',
  qty: item.qty || '1',
  supplier: item.supplier || '',
  destination: item.destination || '',
});

const productDbKey = item =>
  [item.code, item.name, item.supplier].map(v => (v || '').trim().toLowerCase()).join('|');

const readLegacyLocalProductDb = () => {
  try {
    const raw = localStorage.getItem(LEGACY_PRODUCT_DB_KEY);
    const data = raw ? JSON.parse(raw) : [];
    return Array.isArray(data) ? data.map(normalizeDbProduct) : [];
  } catch {
    return [];
  }
};

// ── Calendario interno CRUD ───────────────────────────────────────────────────
export function CalendarPanel({ events, onAdd, onEdit, onDelete, onClose, groupName='', username='' }) {
  const { t } = useTranslation();
  const EMPTY = { title:'', date:'', time:'', location:'', notes:'' };
  const [form, setForm]     = useState(EMPTY);
  const [showForm, setShow] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editFields, setEF]           = useState({});
  const [shareEvId, setShareEvId]     = useState(null);

  const sharePrefix = groupName
    ? `Ciao - Sono Sirus Assistant - AI del Gruppo ${groupName} — Messaggio da ${username}:\n`
    : '';

  const setF   = (k,v) => setForm(f=>({...f,[k]:v}));
  const setEFv = (k,v) => setEF(f=>({...f,[k]:v}));

  const submit = () => {
    if (!form.title.trim() || !form.date) return;
    onAdd(form.title.trim(), form.date, form.time, form.location, form.notes);
    setForm(EMPTY); setShow(false);
  };

  const startEdit = (ev) => { setEditId(ev.id); setEF({title:ev.title,date:ev.date,time:ev.time,location:ev.location,notes:ev.notes}); };
  const saveEdit  = ()   => { onEdit(editId, editFields); setEditId(null); };

  const fmtDate = d => d ? new Date(d+'T00:00:00').toLocaleDateString(undefined,{weekday:'short',day:'2-digit',month:'2-digit'}) : '';

  const today      = new Date().toISOString().split('T')[0];
  const safeEvents = Array.isArray(events) ? events : [];
  const upcoming   = safeEvents.filter(e => e.date >= today).slice(0,20);
  const past       = safeEvents.filter(e => e.date <  today).slice(-5).reverse();

  const cols = ['#00ff41','#88ff99','#00cc33','#44ffaa','#00ff88'];

  return (
    <div className="panel">
      <div className="panel-hdr">
        <span>{t('calendar.title')}</span>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <span className="btn-add-event" onClick={()=>setShow(v=>!v)}>{showForm?'✕':t('calendar.add_btn')}</span>
          <span className="pclose" onClick={onClose}>{t('common.close')}</span>
        </div>
      </div>

      {showForm && (
        <div className="ev-form">
          <div className="form-label">{t('calendar.new_event')}</div>
          <div className="form-grid">
            <div className="form-field form-wide">
              <span className="field-lbl">{t('calendar.title_field')}</span>
              <input className="field-inp" placeholder={t('calendar.placeholder_title')} value={form.title}
                onChange={e=>setF('title',e.target.value)} onKeyDown={e=>e.key==='Enter'&&submit()}/>
            </div>
            <div className="form-field">
              <span className="field-lbl">{t('calendar.date_field')}</span>
              <input className="field-inp field-date" type="date" value={form.date}
                onChange={e=>setF('date',e.target.value)}/>
            </div>
            <div className="form-field">
              <span className="field-lbl">{t('calendar.time_field')}</span>
              <input className="field-inp" type="time" value={form.time}
                onChange={e=>setF('time',e.target.value)}/>
            </div>
            <div className="form-field form-wide">
              <span className="field-lbl">{t('calendar.location_field')}</span>
              <input className="field-inp" placeholder={t('calendar.placeholder_location')} value={form.location}
                onChange={e=>setF('location',e.target.value)}/>
            </div>
            <div className="form-field form-wide">
              <span className="field-lbl">{t('calendar.notes_field')}</span>
              <input className="field-inp" placeholder={t('calendar.placeholder_notes')} value={form.notes}
                onChange={e=>setF('notes',e.target.value)}/>
            </div>
          </div>
          <button className="form-submit" onClick={submit}>{t('calendar.submit')}</button>
        </div>
      )}

      {upcoming.length === 0 && !showForm && <div className="p-empty">{t('calendar.empty')}</div>}

      {upcoming.map((ev,i) => editId===ev.id ? (
        <div className="ev-edit-row" key={ev.id}>
          <input className="edit-inp edit-wide" placeholder={t('calendar.title_field').toLowerCase()} value={editFields.title||''}
            onChange={e=>setEFv('title',e.target.value)}/>
          <input className="edit-inp" type="date" value={editFields.date||''}
            onChange={e=>setEFv('date',e.target.value)}/>
          <input className="edit-inp" type="time" value={editFields.time||''}
            onChange={e=>setEFv('time',e.target.value)}/>
          <input className="edit-inp edit-wide" placeholder={t('calendar.location_field').toLowerCase()} value={editFields.location||''}
            onChange={e=>setEFv('location',e.target.value)}/>
          <div className="ev-edit-btns">
            <button className="btn-save" onClick={saveEdit}>{t('common.save')}</button>
            <button className="btn-cancel" onClick={()=>setEditId(null)}>{t('common.cancel')}</button>
          </div>
        </div>
      ) : (
        <div className="ev-row" key={ev.id}>
          <div className="ev-time">{ev.time||'--:--'}</div>
          <div className="ev-dot" style={{background:cols[i%cols.length],boxShadow:`0 0 6px ${cols[i%cols.length]}`}}/>
          <div style={{flex:1,minWidth:0}}>
            <div className="ev-title">{ev.title}</div>
            <div className="ev-sub">{fmtDate(ev.date)}{ev.location ? ' · '+ev.location : ''}</div>
            {ev.notes && <div className="ev-notes">{ev.notes}</div>}
          </div>
          <div className="ev-actions">
            {shareEvId===ev.id ? (
              <>
                <a className="share-opt-btn" href={`mailto:?subject=${encodeURIComponent('Calendario: '+ev.title)}&body=${encodeURIComponent(sharePrefix+'Calendario:\n► '+ev.title+'\nData: '+ev.date+(ev.time?' '+ev.time:'')+(ev.location?'\nLuogo: '+ev.location:'')+(ev.notes?'\nNote: '+ev.notes:'')+'\n\nGrazie')}`} onClick={()=>setShareEvId(null)}>📧</a>
                <a className="share-opt-btn" href={`https://wa.me/?text=${encodeURIComponent(sharePrefix+'Calendario:\n► '+ev.title+'\nData: '+ev.date+(ev.time?' '+ev.time:'')+(ev.location?'\nLuogo: '+ev.location:'')+(ev.notes?'\nNote: '+ev.notes:'')+'\n\nGrazie')}`} target="_blank" rel="noreferrer" onClick={()=>setShareEvId(null)}>💬</a>
                <span className="ev-share-btn" onClick={()=>setShareEvId(null)}>✕</span>
              </>
            ) : (
              <span className="ev-share-btn" onClick={()=>setShareEvId(ev.id)}>📤</span>
            )}
            <span className="ev-edit-btn" onClick={()=>startEdit(ev)}>✎</span>
            <span className="ev-del-btn" onClick={()=>onDelete(ev.id)}>✕</span>
          </div>
        </div>
      ))}

      {past.length > 0 && <div className="ev-past-label">{t('calendar.past')}</div>}
      {past.map((ev) => (
        <div className="ev-row ev-past" key={ev.id}>
          <div className="ev-time">{ev.time||'--:--'}</div>
          <div className="ev-dot" style={{background:'rgba(0,255,65,0.2)'}}/>
          <div style={{flex:1}}>
            <div className="ev-title" style={{opacity:0.4}}>{ev.title}</div>
            <div className="ev-sub">{fmtDate(ev.date)}</div>
          </div>
          <span className="ev-del-btn" onClick={()=>onDelete(ev.id)}>✕</span>
        </div>
      ))}
    </div>
  );
}

// ── Tasks ─────────────────────────────────────────────────────────────────────
export function TaskPanel({ tasks, onAdd, onToggle, onDelete, onEdit, onClose, groupName='', username='' }) {
  const { t } = useTranslation();
  const [inp, setInp]             = useState('');
  const [pri, setPri]             = useState('media');
  const [editId, setEditId]       = useState(null);
  const [editText, setEditText]   = useState('');
  const [editPri, setEditPri]     = useState('media');
  const [shareExtId, setShareExtId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const submit    = () => { const v=inp.trim(); if(!v) return; onAdd(v,pri); setInp(''); setPri('media'); };
  const startEdit = tk => { setEditId(tk.id); setEditText(tk.text); setEditPri(tk.priority); };
  const saveEdit  = ()  => { onEdit(editId, editText, editPri); setEditId(null); };

  const copyText = (text) => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(() => {});
    } else {
      const ta = document.createElement('textarea');
      ta.value = text; ta.style.cssText = 'position:fixed;opacity:0';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch {}
      document.body.removeChild(ta);
    }
  };

  const toggleSel = (id, e) => {
    e.stopPropagation();
    setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const clearSel = () => setSelectedIds(new Set());

  const sharePrefix = groupName
    ? `Ciao - Sono Sirus Assistant - AI del Gruppo ${groupName} — Messaggio da ${username}:\n`
    : '';

  const safeTasks = Array.isArray(tasks) ? tasks : [];
  const selTasks  = safeTasks.filter(t => selectedIds.has(t.id));
  const multiBody = sharePrefix + 'Task:\n' + selTasks.map(t => `► ${t.text} (${t.priority})`).join('\n') + '\n\nGrazie';

  const badge  = {alta:'badge-h', media:'badge-m', bassa:'badge-l'};
  const blabel = {alta: t('tasks.priority_high'), media: t('tasks.priority_med'), bassa: t('tasks.priority_low')};

  return (
    <div className="panel">
      <div className="panel-hdr">
        <span>{t('tasks.title')}</span>
        <span className="pclose" onClick={onClose}>{t('common.close')}</span>
      </div>
      {safeTasks.length===0 && <div className="p-empty">{t('tasks.empty')}</div>}
      {safeTasks.map(task => editId===task.id ? (
        <div className="task-edit-row" key={task.id}>
          <input className="edit-inp" value={editText} onChange={e=>setEditText(e.target.value)}
            onKeyDown={e=>e.key==='Enter'&&saveEdit()} autoFocus/>
          <select className="pri-sel" value={editPri} onChange={e=>setEditPri(e.target.value)}>
            <option value="alta">{t('tasks.priority_high')}</option>
            <option value="media">{t('tasks.priority_med')}</option>
            <option value="bassa">{t('tasks.priority_low')}</option>
          </select>
          <button className="btn-save" onClick={saveEdit}>{t('common.save')}</button>
          <button className="btn-cancel" onClick={()=>setEditId(null)}>{t('common.cancel')}</button>
        </div>
      ) : (
        <Fragment key={task.id}>
          <div className={`task-row ${task.done?'done-row':''} ${selectedIds.has(task.id)?'sel-row':''}`}>
            <div className="sel-box" onClick={e=>toggleSel(task.id,e)}>{selectedIds.has(task.id)?'■':'□'}</div>
            <div className={`chk ${task.done?'chk-on':''}`} onClick={()=>onToggle(task.id)}>{task.done?'✓':'○'}</div>
            <div className={`task-txt ${task.done?'struck':''}`} onClick={()=>onToggle(task.id)}>{task.text}</div>
            <div className={`badge ${badge[task.priority]}`}>{blabel[task.priority]}</div>
            <span className="task-ext-share-btn" onClick={e=>{e.stopPropagation();setShareExtId(shareExtId===task.id?null:task.id)}}>📤</span>
            <div className="task-edit-btn" onClick={()=>startEdit(task)}>✎</div>
            <div className="task-del" onClick={()=>onDelete(task.id)}>✕</div>
          </div>
          {shareExtId===task.id && (() => {
            const isMulti   = selectedIds.size > 0 && selectedIds.has(task.id);
            const singleBody = sharePrefix+'Task:\n► '+task.text+'\nPriorita: '+task.priority+'\n\nGrazie';
            const body      = isMulti ? multiBody : singleBody;
            const subject   = isMulti ? 'Task da SIRUS' : 'Task: '+task.text;
            const onSent    = () => { setShareExtId(null); if (isMulti) clearSel(); };
            return (
              <div className="shop-share-row">
                <a className="share-btn-item" href={`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`} onClick={onSent}>📧 Email</a>
                <a className="share-btn-item" href={`sms:?body=${encodeURIComponent(body)}`} onClick={onSent}>📱 SMS</a>
                <a className="share-btn-item" href={`https://wa.me/?text=${encodeURIComponent(body)}`} target="_blank" rel="noreferrer" onClick={onSent}>💬 WhatsApp</a>
                <span className="share-btn-item" onClick={()=>{copyText(body);onSent();}}>📋 Copia</span>
                <span className="share-btn-close" onClick={()=>setShareExtId(null)}>✕</span>
              </div>
            );
          })()}
        </Fragment>
      ))}
      {selectedIds.size > 0 && (
        <div className="multi-share-bar">
          <span className="multi-share-count">{selectedIds.size} selezionati</span>
          <a className="share-opt-btn-bright" href={`mailto:?subject=${encodeURIComponent('Task da SIRUS')}&body=${encodeURIComponent(multiBody)}`} onClick={clearSel} title="Email">📧</a>
          <a className="share-opt-btn-bright" href={`sms:?body=${encodeURIComponent(multiBody)}`} onClick={clearSel} title="SMS">📱</a>
          <a className="share-opt-btn-bright" href={`https://wa.me/?text=${encodeURIComponent(multiBody)}`} target="_blank" rel="noreferrer" onClick={clearSel} title="WhatsApp">💬</a>
          <span className="share-opt-btn-bright" onClick={()=>{copyText(multiBody);clearSel();}} title="Copia testo">📋</span>
          <button className="add-btn" onClick={clearSel}>✕</button>
        </div>
      )}
      <div className="add-row">
        <span className="prompt-sym">&gt;</span>
        <input className="add-inp" placeholder={t('tasks.placeholder')} value={inp}
          onChange={e=>setInp(e.target.value)} onKeyDown={e=>e.key==='Enter'&&submit()}/>
        <select className="pri-sel" value={pri} onChange={e=>setPri(e.target.value)}>
          <option value="alta">{t('tasks.priority_high')}</option>
          <option value="media">{t('tasks.priority_med')}</option>
          <option value="bassa">{t('tasks.priority_low')}</option>
        </select>
        <button className="add-btn" onClick={submit}>{t('tasks.add')}</button>
      </div>
    </div>
  );
}

// ── Shopping Panel base ───────────────────────────────────────────────────────
function ShoppingPanelBase({ titleKey, items, onAdd, onRemove, onEdit, onClose, groupName='', username='',
  productsDb=[], onProductsDbChange=()=>{}, isAdmin=false }) {
  const { t } = useTranslation();
  const EMPTY = { code:'', name:'', qty:'1', supplier:'', destination:'' };
  const [sortField, setSortField] = useState(null);
  const [sortDir,   setSortDir]   = useState('asc');
  const handleSort = f => {
    if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(f); setSortDir('asc'); }
  };
  const [form, setForm]     = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [editFields, setEF] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [shareShopId, setShareShopId] = useState(null);
  const safeProductsDb = Array.isArray(productsDb) ? productsDb.map(normalizeDbProduct) : [];
  const [dbOpen, setDbOpen] = useState(false);
  const [dbFeedback, setDbFeedback] = useState('');
  const [dbEditId, setDbEditId] = useState(null);
  const [dbEditFields, setDbEditFields] = useState({});
  const [importText, setImportText] = useState('');
  const [importSupplier, setImportSupplier] = useState('');
  const [legacyLocal, setLegacyLocal] = useState([]);
  const [dbSearchOpen, setDbSearchOpen] = useState(false);
  const [dbSearchText, setDbSearchText] = useState('');
  const dbSearchRef = useRef(null);
  const feedbackTimer = useRef(null);

  // Migrazione una-tantum: se questo browser ha ancora dati del vecchio database
  // solo-locale, l'admin può importarli nel database condiviso appena apre la modale.
  useEffect(() => {
    if (dbOpen && isAdmin) setLegacyLocal(readLegacyLocalProductDb());
  }, [dbOpen, isAdmin]);

  // Ricerca nel database dal form manuale: chiusura al click fuori dal popover.
  useEffect(() => {
    if (!dbSearchOpen) return;
    const onDocClick = e => {
      if (dbSearchRef.current && !dbSearchRef.current.contains(e.target)) {
        setDbSearchOpen(false);
        setDbSearchText('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [dbSearchOpen]);

  const set  = (k,v) => setForm(f=>({...f,[k]:v}));
  const setE = (k,v) => setEF(f=>({...f,[k]:v}));

  const copyText = (text) => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(() => {});
    } else {
      const ta = document.createElement('textarea');
      ta.value = text; ta.style.cssText = 'position:fixed;opacity:0';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch {}
      document.body.removeChild(ta);
    }
  };

  const submit    = () => { if(!form.name.trim()) return; onAdd(form.name.trim(),parseInt(form.qty)||1,form.destination,form.supplier,'',form.code); setForm(EMPTY); };
  const startEdit = it => { setEditId(it.id); setEF({code:it.code||'',name:it.name,qty:it.qty,supplier:it.supplier,destination:it.destination}); };
  const saveEdit  = ()  => { onEdit(editId,editFields); setEditId(null); };
  const showDbFeedback = text => {
    setDbFeedback(text);
    window.clearTimeout(feedbackTimer.current);
    feedbackTimer.current = window.setTimeout(() => setDbFeedback(''), 2500);
  };
  // Scritture sul database condiviso: il controllo duplicati per il messaggio usa
  // la fotografia più recente nota (prop, tenuta fresca da SSE/polling); la vera
  // scrittura passa sempre per l'updater funzionale di setState, che React esegue
  // sullo stato più aggiornato — evita di perdere modifiche concorrenti di altri utenti.
  const addFormToDb = () => {
    if (!isAdmin || !form.name.trim()) return;
    const item = normalizeDbProduct({ ...form, id: Date.now(), name: form.name.trim(), qty: parseInt(form.qty) || 1 });
    if (safeProductsDb.some(p => productDbKey(p) === productDbKey(item))) {
      showDbFeedback('Prodotto gia presente nel database');
      return;
    }
    onProductsDbChange(prev => {
      const current = Array.isArray(prev) ? prev.map(normalizeDbProduct) : [];
      if (current.some(p => productDbKey(p) === productDbKey(item))) return current;
      return [item, ...current];
    });
    showDbFeedback('Prodotto aggiunto al database');
  };
  const addDbItemToList = item => {
    onAdd(item.name, parseInt(item.qty) || 1, item.destination, item.supplier, '', item.code);
    showDbFeedback('Prodotto aggiunto alla lista');
  };
  const toggleDbSearch = () => {
    setDbSearchOpen(o => {
      if (o) setDbSearchText('');
      return !o;
    });
  };
  // Selezionare un risultato precompila il form (nessuna aggiunta automatica alla
  // lista): l'utente resta libero di correggere la quantità prima di confermare.
  const pickDbSearchResult = item => {
    setForm(f => ({
      ...f,
      code: item.code || '',
      name: item.name || '',
      qty: item.qty ? String(item.qty) : f.qty,
      supplier: item.supplier || '',
      destination: item.destination || '',
    }));
    setDbSearchOpen(false);
    setDbSearchText('');
  };
  const startDbEdit = item => {
    if (!isAdmin) return;
    setDbEditId(item.id);
    setDbEditFields({ code:item.code||'', name:item.name||'', qty:item.qty||1, supplier:item.supplier||'', destination:item.destination||'' });
  };
  const setDbField = (k,v) => setDbEditFields(f => ({ ...f, [k]: v }));
  const saveDbEdit = () => {
    if (!isAdmin || !dbEditFields.name?.trim()) return;
    const nextItem = normalizeDbProduct({ ...dbEditFields, id: dbEditId, name: dbEditFields.name.trim(), qty: parseInt(dbEditFields.qty) || 1 });
    if (safeProductsDb.some(p => p.id !== dbEditId && productDbKey(p) === productDbKey(nextItem))) {
      showDbFeedback('Prodotto gia presente nel database');
      return;
    }
    onProductsDbChange(prev => {
      const current = Array.isArray(prev) ? prev.map(normalizeDbProduct) : [];
      return current.map(p => p.id === dbEditId ? nextItem : p);
    });
    setDbEditId(null);
    showDbFeedback('Database aggiornato');
  };
  const deleteDbItem = id => {
    if (!isAdmin) return;
    if (!window.confirm('Vuoi eliminare questo prodotto dal database?')) return;
    onProductsDbChange(prev => (Array.isArray(prev) ? prev.map(normalizeDbProduct) : []).filter(p => p.id !== id));
    showDbFeedback('Prodotto eliminato dal database');
  };
  const parseImportLine = line => {
    const trimmed = line.trim();
    if (!trimmed) return null;
    const idx = trimmed.indexOf(' - ');
    if (idx === -1) return null;
    const code = trimmed.slice(0, idx).trim();
    const name = trimmed.slice(idx + 3).trim();
    if (!code || !name) return null;
    return { code, name };
  };
  const runImport = () => {
    if (!isAdmin) return;
    const seen = new Set(safeProductsDb.map(productDbKey));
    const added = [];
    let invalid = 0, duplicate = 0;
    importText.split('\n').forEach((line, i) => {
      const parsed = parseImportLine(line);
      if (!parsed) { if (line.trim()) invalid++; return; }
      const item = normalizeDbProduct({ id: Date.now() + i, code: parsed.code, name: parsed.name, qty: 1, supplier: importSupplier.trim(), destination: '' });
      const key = productDbKey(item);
      if (seen.has(key)) { duplicate++; return; }
      seen.add(key);
      added.push(item);
    });
    if (added.length) {
      onProductsDbChange(prev => {
        const current = Array.isArray(prev) ? prev.map(normalizeDbProduct) : [];
        const freshSeen = new Set(current.map(productDbKey));
        const safeAdded = added.filter(a => !freshSeen.has(productDbKey(a)));
        return [...safeAdded, ...current];
      });
    }
    showDbFeedback(`${added.length} aggiunti, ${duplicate} duplicati saltati${invalid ? `, ${invalid} righe non valide` : ''}`);
    setImportText('');
  };
  const migrateLegacyLocal = () => {
    if (!isAdmin || !legacyLocal.length) return;
    const legacyNormalized = legacyLocal.map(normalizeDbProduct);
    onProductsDbChange(prev => {
      const current = Array.isArray(prev) ? prev.map(normalizeDbProduct) : [];
      const seen = new Set(current.map(productDbKey));
      const toAdd = legacyNormalized.filter(p => !seen.has(productDbKey(p)));
      return [...toAdd, ...current];
    });
    showDbFeedback(`${legacyLocal.length} prodotti locali migrati nel database condiviso`);
    setLegacyLocal([]);
  };

  const toggleSel = (id, e) => {
    e.stopPropagation();
    setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const clearSel = () => setSelectedIds(new Set());

  const sharePrefix = groupName
    ? `Ciao - Sono Sirus Assistant - AI del Gruppo ${groupName} — Messaggio da ${username}:\n`
    : '';

  const safeItems = Array.isArray(items) ? items : [];
  const sortedItems = sortField
    ? [...safeItems].sort((a, b) => {
        const va = (a[sortField] || '').toString().toLowerCase();
        const vb = (b[sortField] || '').toString().toLowerCase();
        return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      })
    : safeItems;
  const selItems  = safeItems.filter(i => selectedIds.has(i.id));
  const dbSearchQuery = dbSearchText.trim().toLowerCase();
  const dbSearchResults = dbSearchQuery
    ? safeProductsDb.filter(p =>
        (p.name||'').toLowerCase().includes(dbSearchQuery) ||
        (p.code||'').toLowerCase().includes(dbSearchQuery) ||
        (p.supplier||'').toLowerCase().includes(dbSearchQuery)
      ).slice(0, 8)
    : [];
  const orderBody = (list, supplier='') =>
    sharePrefix +
    (supplier ? `ORDINE FORNITORE: ${supplier}\n\n` : 'Lista Spesa:\n') +
    'Codice | Prodotto | Quantità | Note\n' +
    list.map(i => `${i.code||''} | ${i.name} | ${i.qty||''} | ${i.destination||''}`).join('\n') +
    '\n\nGrazie';
  const multiBody = orderBody(selItems);

  return (
    <div className="panel">
      <div className="panel-hdr">
        <span>// {t(titleKey)}</span>
        <span className="pclose" onClick={onClose}>{t('common.close')}</span>
      </div>
      {safeItems.length > 0 && (
        <div className="shop-table">
          <div className="shop-thead">
            <div className="sth sel-col"/>
            <div className="sth sth-qty">{t('shop.qty_header')}</div>
            <div className="sth sth-code sth-sortable" onClick={()=>handleSort('code')}>{t('shop.code_header')}{sortField==='code'?(sortDir==='asc'?' ↑':' ↓'):''}</div>
            <div className="sth sth-name sth-sortable" onClick={()=>handleSort('name')}>{t('shop.desc_header')}{sortField==='name'?(sortDir==='asc'?' ↑':' ↓'):''}</div>
            <div className="sth sth-sup sth-sortable" onClick={()=>handleSort('supplier')}>{t('shop.supplier_header')}{sortField==='supplier'?(sortDir==='asc'?' ↑':' ↓'):''}</div>
            <div className="sth sth-dest">{t('shop.dest_header')}</div>
            <div className="sth sth-del"></div>
          </div>
          {sortedItems.map(it => {
            const sameSupplier = it.supplier
              ? safeItems.filter(x => (x.supplier||'').trim().toLowerCase() === it.supplier.trim().toLowerCase())
              : [it];
            const shareBody = orderBody(sameSupplier, it.supplier || '');
            return editId===it.id ? (
              <div className="shop-edit-row" key={it.id}>
                <input className="edit-inp edit-qty" type="number" value={editFields.qty ?? ''} onChange={e=>setE('qty',e.target.value)}/>
                <input className="edit-inp edit-code" placeholder={t('shop.code_field').toLowerCase()} value={editFields.code||''} onChange={e=>setE('code',e.target.value)}/>
                <input className="edit-inp" placeholder={t('shop.desc_field').toLowerCase()} value={editFields.name||''} onChange={e=>setE('name',e.target.value)}/>
                <input className="edit-inp" placeholder={t('shop.supplier_field').toLowerCase()} value={editFields.supplier||''} onChange={e=>setE('supplier',e.target.value)}/>
                <input className="edit-inp" placeholder={t('shop.dest_field').toLowerCase()} value={editFields.destination||''} onChange={e=>setE('destination',e.target.value)}/>
                <button className="btn-save" onClick={saveEdit}>{t('common.save')}</button>
                <button className="btn-cancel" onClick={()=>setEditId(null)}>{t('common.cancel')}</button>
              </div>
            ) : (
              <Fragment key={it.id}>
                <div className={`shop-tr ${selectedIds.has(it.id)?'sel-row':''}`}>
                  <div className="stc sel-col"><span className="sel-box" onClick={e=>toggleSel(it.id,e)}>{selectedIds.has(it.id)?'■':'□'}</span></div>
                  <div className="stc sth-qty">×{it.qty}</div>
                  <div className="stc sth-code">{it.code||'—'}</div>
                  <div className="stc sth-name">{it.name}</div>
                  <div className="stc sth-sup">{it.supplier||'—'}</div>
                  <div className="stc sth-dest">{it.destination||'—'}</div>
                  <div className="stc sth-actions">
                    {shareShopId===it.id ? (
                      <span className="task-ext-share-btn" onClick={()=>setShareShopId(null)}>✕</span>
                    ) : (
                      <span className="task-ext-share-btn" onClick={e=>{e.stopPropagation();setShareShopId(it.id)}}>📤</span>
                    )}
                    <span className="shop-edit-btn" onClick={()=>startEdit(it)}>✎</span>
                    <span className="sth-del" onClick={()=>onRemove(it.id)}>✕</span>
                  </div>
                </div>
                {shareShopId===it.id && (() => {
                  const isMulti = selectedIds.size > 0 && selectedIds.has(it.id);
                  const body    = isMulti ? multiBody : shareBody;
                  const subject = isMulti ? 'Lista Spesa da SIRUS' : 'Lista Spesa: '+it.name;
                  const onSent  = () => { setShareShopId(null); if (isMulti) clearSel(); };
                  return (
                    <div className="shop-share-row">
                      <a className="share-btn-item" href={`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`} onClick={onSent}>📧 Email</a>
                      <a className="share-btn-item" href={`sms:?body=${encodeURIComponent(body)}`} onClick={onSent}>📱 SMS</a>
                      <a className="share-btn-item" href={`https://wa.me/?text=${encodeURIComponent(body)}`} target="_blank" rel="noreferrer" onClick={onSent}>💬 WhatsApp</a>
                      <span className="share-btn-item" onClick={()=>{copyText(body);onSent();}}>📋 Copia</span>
                      <span className="share-btn-close" onClick={()=>setShareShopId(null)}>✕</span>
                    </div>
                  );
                })()}
              </Fragment>
            );
          })}
        </div>
      )}
      {items.length===0 && <div className="p-empty">{t('shop.empty')}</div>}
      {selectedIds.size > 0 && (
        <div className="multi-share-bar">
          <span className="multi-share-count">{selectedIds.size} selezionati</span>
          <a className="share-opt-btn-bright" href={`mailto:?subject=${encodeURIComponent('Lista Spesa da SIRUS')}&body=${encodeURIComponent(multiBody)}`} onClick={clearSel} title="Email">📧</a>
          <a className="share-opt-btn-bright" href={`sms:?body=${encodeURIComponent(multiBody)}`} onClick={clearSel} title="SMS">📱</a>
          <a className="share-opt-btn-bright" href={`https://wa.me/?text=${encodeURIComponent(multiBody)}`} target="_blank" rel="noreferrer" onClick={clearSel} title="WhatsApp">💬</a>
          <span className="share-opt-btn-bright" onClick={()=>{copyText(multiBody);clearSel();}} title="Copia testo">📋</span>
          <button className="add-btn" onClick={clearSel}>✕</button>
        </div>
      )}
      <div className="shop-form">
        <div className="form-label">{t('shop.new_item')}</div>
        <div className="form-grid">
          <div className="form-field form-wide">
            <span className="field-lbl">{t('shop.code_field')}</span>
            <input className="field-inp" placeholder={t('shop.placeholder_code')} value={form.code}
              onChange={e=>set('code',e.target.value)}/>
          </div>
          <div className="form-field form-wide db-search-field" ref={dbSearchRef}>
            <div className="field-lbl-row">
              <span className="field-lbl">{t('shop.desc_field')}</span>
              <button type="button" className="db-search-btn" title="Cerca nel database" onClick={toggleDbSearch}>🔍</button>
            </div>
            <input className="field-inp" placeholder={t('shop.placeholder_name')} value={form.name}
              onChange={e=>set('name',e.target.value)} onKeyDown={e=>e.key==='Enter'&&submit()}/>
            {dbSearchOpen && (
              <div className="db-search-pop">
                <input
                  className="db-search-inp"
                  autoFocus
                  placeholder="Cerca per codice, nome o fornitore..."
                  value={dbSearchText}
                  onChange={e=>setDbSearchText(e.target.value)}
                  onKeyDown={e=>{ if (e.key==='Escape') { setDbSearchOpen(false); setDbSearchText(''); } }}
                />
                <div className="db-search-results">
                  {!dbSearchQuery ? (
                    <div className="db-search-hint">Digita per cercare nel database</div>
                  ) : dbSearchResults.length === 0 ? (
                    <div className="db-search-hint">Nessun prodotto trovato</div>
                  ) : dbSearchResults.map(item => (
                    <div key={item.id} className="db-search-item" onClick={()=>pickDbSearchResult(item)}>
                      <span className="db-search-item-code">{item.code || '—'}</span>
                      <span className="db-search-item-name">{item.name}</span>
                      {item.supplier && <span className="db-search-item-sup">{item.supplier}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="form-field">
            <span className="field-lbl">{t('shop.qty_field')}</span>
            <input className="field-inp" type="number" min="1" value={form.qty} onChange={e=>set('qty',e.target.value)}/>
          </div>
          <div className="form-field">
            <span className="field-lbl">{t('shop.supplier_field')}</span>
            <input className="field-inp" placeholder={t('shop.placeholder_supplier')} value={form.supplier} onChange={e=>set('supplier',e.target.value)}/>
          </div>
          <div className="form-field form-wide">
            <span className="field-lbl">{t('shop.dest_field')}</span>
            <input className="field-inp" placeholder={t('shop.placeholder_dest')} value={form.destination} onChange={e=>set('destination',e.target.value)}/>
          </div>
        </div>
        <div className="shop-form-actions">
          <button className="form-submit form-submit-small" onClick={submit}>Aggiungi alla Lista</button>
          {isAdmin && <button className="form-submit form-submit-small" onClick={addFormToDb}>Aggiungi al Database</button>}
          <button className="form-submit form-submit-small" onClick={()=>setDbOpen(true)}>Database</button>
        </div>
        {dbFeedback && <div className="shop-db-feedback">{dbFeedback}</div>}
      </div>
      {dbOpen && (
        <div className="shop-db-modal" role="dialog" aria-modal="true">
          <div className="shop-db-box">
            <div className="shop-db-head">
              <span>// DATABASE</span>
              <span className="pclose" onClick={()=>{ setDbOpen(false); setDbEditId(null); }}>[X]</span>
            </div>
            {dbFeedback && <div className="shop-db-feedback shop-db-feedback-modal">{dbFeedback}</div>}
            {isAdmin && legacyLocal.length > 0 && (
              <div className="shop-db-feedback-modal" style={{display:'flex', alignItems:'center', gap:8, flexWrap:'wrap'}}>
                <span>⚠ Trovati {legacyLocal.length} prodotti salvati solo su questo browser (vecchio sistema).</span>
                <button className="form-submit form-submit-small" style={{flex:'none'}} onClick={migrateLegacyLocal}>Migra nel database condiviso</button>
              </div>
            )}
            {isAdmin && (
              <>
                <div className="form-field" style={{marginBottom:10}}>
                  <span className="field-lbl">Importa lista (una riga per prodotto: CODICE - NOME)</span>
                  <textarea className="field-inp" style={{width:'100%',minHeight:100,fontFamily:'inherit'}} value={importText}
                    onChange={e=>setImportText(e.target.value)} placeholder={'MUSHPO2 - Porcini\nPGRO - Pinot Blush'} />
                </div>
                <div className="form-grid">
                  <div className="form-field form-wide">
                    <span className="field-lbl">Fornitore (applicato a tutte le righe)</span>
                    <input className="field-inp" placeholder="es. Divine" value={importSupplier} onChange={e=>setImportSupplier(e.target.value)}/>
                  </div>
                </div>
                <div className="shop-form-actions">
                  <button className="form-submit form-submit-small" onClick={runImport} disabled={!importText.trim()}>Importa</button>
                </div>
              </>
            )}
            {safeProductsDb.length === 0 ? (
              <div className="p-empty">Nessun prodotto salvato nel database</div>
            ) : (
              <div className="shop-db-table">
                <div className="shop-db-row shop-db-thead">
                  <div>Codice</div><div>Prodotto</div><div>QTA</div><div>Fornitore</div><div>Note</div><div></div>
                </div>
                {safeProductsDb.map(item => dbEditId === item.id ? (
                  <div className="shop-db-edit" key={item.id}>
                    <input className="edit-inp edit-code" value={dbEditFields.code||''} onChange={e=>setDbField('code',e.target.value)} placeholder="codice"/>
                    <input className="edit-inp" value={dbEditFields.name||''} onChange={e=>setDbField('name',e.target.value)} placeholder="prodotto"/>
                    <input className="edit-inp edit-qty" type="number" min="1" value={dbEditFields.qty ?? ''} onChange={e=>setDbField('qty',e.target.value)}/>
                    <input className="edit-inp" value={dbEditFields.supplier||''} onChange={e=>setDbField('supplier',e.target.value)} placeholder="fornitore"/>
                    <input className="edit-inp" value={dbEditFields.destination||''} onChange={e=>setDbField('destination',e.target.value)} placeholder="note"/>
                    <button className="btn-save" onClick={saveDbEdit}>{t('common.save')}</button>
                    <button className="btn-cancel" onClick={()=>setDbEditId(null)}>{t('common.cancel')}</button>
                  </div>
                ) : (
                  <div className="shop-db-row" key={item.id}>
                    <div data-label="Codice">{item.code || '—'}</div>
                    <div data-label="Prodotto">{item.name}</div>
                    <div data-label="Qtà">{item.qty || 1}</div>
                    <div data-label="Fornitore">{item.supplier || '—'}</div>
                    <div data-label="Note">{item.destination || '—'}</div>
                    <div className="shop-db-actions">
                      <button className="btn-save" onClick={()=>addDbItemToList(item)}>ADD+ alla Lista</button>
                      {isAdmin && <button className="btn-save" onClick={()=>startDbEdit(item)}>Mod</button>}
                      {isAdmin && <button className="btn-cancel" onClick={()=>deleteDbItem(item.id)}>Elimina</button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function ShoppingPanel(props)      { return <ShoppingPanelBase {...props} titleKey="shop.personal_title"/>; }
export function GroupShoppingPanel(props) { return <ShoppingPanelBase {...props} titleKey="shop.group_title"/>; }

// ── Group Chat ────────────────────────────────────────────────────────────────
export function GroupChatPanel({ user, groupUsers: groupUsersProp, sendNotification, onClose }) {
  const { messages, archive, loading, sendMessage, loadArchive, exportBackup } = useGroupChat(user);
  const [inp, setInp]               = useState('');
  const [showArchive, setShowArchive] = useState(false);
  const [mentionQ, setMentionQ]     = useState(null);
  const [localUsers, setLocalUsers] = useState(groupUsersProp || []);
  const bottomRef = useRef(null);

  // Fetch fresh user list every time the panel opens
  useEffect(() => {
    if (!WORKER_URL) return;
    fetch(`${WORKER_URL}/api/users`, { headers: authHeaders(), credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.users?.length) setLocalUsers(d.users); })
      .catch(() => {});
  }, []);

  const groupUsers = localUsers;

  useEffect(() => {
    if (!showArchive && bottomRef.current)
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages, showArchive]);

  const handleInpChange = (e) => {
    const val = e.target.value;
    setInp(val);
    const lastWord = val.split(/\s/).pop();
    if (lastWord.startsWith('@') && lastWord.length >= 1) {
      setMentionQ(lastWord.slice(1).toLowerCase());
    } else {
      setMentionQ(null);
    }
  };

  const pickMention = (username) => {
    const words = inp.trimEnd().split(/\s+/);
    words[words.length - 1] = `@${username}`;
    setInp(words.join(' ') + ' ');
    setMentionQ(null);
  };

  const matchedUsers = mentionQ !== null && groupUsers
    ? groupUsers.filter(u => u.username.toLowerCase().startsWith(mentionQ))
    : [];

  const send = () => {
    const v = inp.trim();
    if (!v) return;
    // send notifications for @mentions
    if (sendNotification && groupUsers) {
      const re = /@(\w+)/g;
      let m;
      while ((m = re.exec(v)) !== null) {
        const target = groupUsers.find(u => u.username.toLowerCase() === m[1].toLowerCase());
        if (target) sendNotification(target.id, target.username, 'mention', v);
      }
    }
    sendMessage(v);
    setInp('');
    setMentionQ(null);
  };

  const toggleArchive = () => {
    if (!showArchive) loadArchive();
    setShowArchive(v => !v);
  };

  const fmtTime = iso => {
    const d = new Date(iso);
    const isToday = d.toDateString() === new Date().toDateString();
    const time = d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    return isToday ? time
      : d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }) + ' ' + time;
  };

  const renderText = (text) =>
    text.split(/(@\w+)/g).map((part, i) =>
      /^@\w+$/.test(part)
        ? <span key={i} className="chat-mention">{part}</span>
        : part
    );

  const renderMsg = m => (
    <div className={`chat-msg ${m.userId === user?.id ? 'chat-own' : ''}`} key={m.id}>
      <div className="chat-meta">
        <span className="chat-user">{m.username}</span>
        <span className="chat-time">{fmtTime(m.createdAt)}</span>
      </div>
      <div className="chat-text">{renderText(m.text)}</div>
    </div>
  );

  const displayed = showArchive ? archive : messages;

  return (
    <div className="panel chat-panel">
      <div className="panel-hdr">
        <span>// GROUP CHAT</span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span className="chat-arch-btn" onClick={toggleArchive}>
            {showArchive ? '← LIVE' : 'ARCHIVIO'}
          </span>
          <span className="chat-export-btn" onClick={exportBackup} title="Scarica backup">⬇</span>
          <span className="pclose" onClick={onClose}>[X]</span>
        </div>
      </div>

      <div className="chat-msgs">
        {loading && !showArchive && <div className="p-empty">caricamento...</div>}
        {!loading && displayed.length === 0 && (
          <div className="p-empty">
            {showArchive ? 'archivio vuoto' : 'nessun messaggio — scrivi il primo!'}
          </div>
        )}
        {displayed.map(renderMsg)}
        {!showArchive && <div ref={bottomRef} />}
      </div>

      {!showArchive && (
        <div className="add-row" style={{ position: 'relative' }}>
          {matchedUsers.length > 0 && (
            <div className="mention-dropdown">
              {matchedUsers.map(u => (
                <div key={u.id} className="mention-opt" onMouseDown={() => pickMention(u.username)}>
                  @{u.username}
                </div>
              ))}
            </div>
          )}
          <span className="prompt-sym">&gt;</span>
          <input
            className="add-inp"
            placeholder="scrivi un messaggio... (@nome per menzionare)"
            value={inp}
            onChange={handleInpChange}
            onKeyDown={e => e.key === 'Enter' && send()}
          />
          <button className="add-btn" onClick={send}>INVIA</button>
        </div>
      )}
    </div>
  );
}

// ── Contacts ──────────────────────────────────────────────────────────────────
export function ContactsPanel({ contacts, onAdd, onEdit, onDelete, onClose }) {
  const EMPTY = { name:'', phone:'', email:'', notes:'' };
  const [form, setForm]     = useState(EMPTY);
  const [showForm, setShow] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editFields, setEF] = useState({});
  const [search, setSearch] = useState('');

  const setF   = (k,v) => setForm(f=>({...f,[k]:v}));
  const setEFv = (k,v) => setEF(f=>({...f,[k]:v}));

  const submit    = () => { if(!form.name.trim()) return; onAdd(form.name.trim(),form.phone,form.email,form.notes); setForm(EMPTY); setShow(false); };
  const startEdit = c  => { setEditId(c.id); setEF({name:c.name,phone:c.phone,email:c.email,notes:c.notes}); };
  const saveEdit  = ()  => { onEdit(editId, editFields); setEditId(null); };

  const safeContacts = Array.isArray(contacts) ? contacts : [];
  const filtered     = safeContacts.filter(c =>
    !search.trim() ||
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.phone.includes(search) ||
    c.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="panel">
      <div className="panel-hdr">
        <span>// RUBRICA</span>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <span className="btn-add-event" onClick={()=>{ setShow(v=>!v); setEditId(null); }}>
            {showForm?'✕':'+ CONTATTO'}
          </span>
          <span className="pclose" onClick={onClose}>[X]</span>
        </div>
      </div>

      {showForm && (
        <div className="ev-form">
          <div className="form-label">NUOVO CONTATTO</div>
          <div className="form-grid">
            <div className="form-field form-wide">
              <span className="field-lbl">NOME *</span>
              <input className="field-inp" placeholder="nome e cognome" value={form.name}
                onChange={e=>setF('name',e.target.value)} onKeyDown={e=>e.key==='Enter'&&submit()} autoFocus/>
            </div>
            <div className="form-field">
              <span className="field-lbl">TELEFONO</span>
              <input className="field-inp" placeholder="+39 333..." type="tel" value={form.phone}
                onChange={e=>setF('phone',e.target.value)}/>
            </div>
            <div className="form-field">
              <span className="field-lbl">EMAIL</span>
              <input className="field-inp" placeholder="email@..." type="email" value={form.email}
                onChange={e=>setF('email',e.target.value)}/>
            </div>
            <div className="form-field form-wide">
              <span className="field-lbl">NOTE</span>
              <input className="field-inp" placeholder="note opzionali..." value={form.notes}
                onChange={e=>setF('notes',e.target.value)}/>
            </div>
          </div>
          <button className="form-submit" onClick={submit}>SALVA CONTATTO</button>
        </div>
      )}

      {safeContacts.length > 4 && (
        <div className="contact-search-bar">
          <input className="add-inp" placeholder="cerca contatto..." value={search}
            onChange={e=>setSearch(e.target.value)}/>
        </div>
      )}

      {filtered.length === 0 && (
        <div className="p-empty">
          {safeContacts.length === 0 ? 'nessun contatto — premi + CONTATTO' : 'nessun risultato'}
        </div>
      )}

      {filtered.map(c => editId===c.id ? (
        <div className="contact-edit-row" key={c.id}>
          <input className="edit-inp" placeholder="nome" value={editFields.name||''} onChange={e=>setEFv('name',e.target.value)} autoFocus/>
          <input className="edit-inp" placeholder="telefono" value={editFields.phone||''} onChange={e=>setEFv('phone',e.target.value)}/>
          <input className="edit-inp" placeholder="email" value={editFields.email||''} onChange={e=>setEFv('email',e.target.value)}/>
          <input className="edit-inp edit-wide" placeholder="note" value={editFields.notes||''} onChange={e=>setEFv('notes',e.target.value)}/>
          <button className="btn-save" onClick={saveEdit}>✓</button>
          <button className="btn-cancel" onClick={()=>setEditId(null)}>✕</button>
        </div>
      ) : (
        <div className="contact-row" key={c.id}>
          <div className="contact-avatar">{c.name.charAt(0).toUpperCase()}</div>
          <div className="contact-info">
            <div className="contact-name">{c.name}</div>
            <div className="contact-details">
              {c.phone && <a className="contact-link contact-phone" href={`tel:${c.phone}`}>{c.phone}</a>}
              {c.email && <a className="contact-link contact-email" href={`mailto:${c.email}`}>{c.email}</a>}
              {c.notes && <span className="contact-notes">{c.notes}</span>}
            </div>
          </div>
          <div className="contact-actions">
            <span className="ev-edit-btn" onClick={()=>startEdit(c)}>✎</span>
            <span className="ev-del-btn" onClick={()=>onDelete(c.id)}>✕</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Orders ────────────────────────────────────────────────────────────────────
const ORDER_EMPTY = { tipo: '', numero_tavolo: '', starter: '', main: '', dessert: '', note: '' };

function fmtOrderTime(ts) {
  if (!ts) return '--:--';
  const d = new Date(ts);
  const isToday = d.toDateString() === new Date().toDateString();
  const time = d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  return isToday ? time : d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }) + ' ' + time;
}

export function OrdersPanel({ orders, onAdd, onUpdate, onComplete, onDelete, onClose, user }) {
  const [form, setForm]     = useState(ORDER_EMPTY);
  const [showForm, setShow] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editFields, setEF] = useState({});
  const [adminErr, setAdminErr] = useState('');

  const isAdmin = user?.role === 'admin';

  const setF   = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setEFv = (k, v) => setEF(f => ({ ...f, [k]: v }));

  const submit = () => {
    if (!form.tipo && !form.numero_tavolo && !form.main) return;
    onAdd({ ...form, created_by: user?.username || '' });
    setForm(ORDER_EMPTY);
    setShow(false);
  };

  const startEdit = (o) => { setEditId(o.id); setEF({ tipo: o.tipo, numero_tavolo: o.numero_tavolo, starter: o.starter, main: o.main, dessert: o.dessert, note: o.note }); };
  const saveEdit  = ()   => { onUpdate(editId, editFields); setEditId(null); };

  const handleComplete = (o) => {
    if (!isAdmin) { setAdminErr('Solo l\'admin può concludere un ordine.'); setTimeout(() => setAdminErr(''), 3000); return; }
    onComplete(o.id, user?.username);
  };
  const handleDelete = (o) => {
    if (!isAdmin) { setAdminErr('Solo l\'admin può eliminare un ordine.'); setTimeout(() => setAdminErr(''), 3000); return; }
    onDelete(o.id);
  };

  const safeOrders = Array.isArray(orders) ? orders : [];
  const active    = safeOrders.filter(o => o.status === 'active');
  const completed = safeOrders.filter(o => o.status === 'completed');

  const OrderRow = ({ o }) => editId === o.id ? (
    <div className="order-edit-row" key={o.id}>
      <select className="order-field-inp order-tipo-sel" value={editFields.tipo || ''} onChange={e => setEFv('tipo', e.target.value)}>
        <option value="">Tipo</option>
        <option value="eat in">Eat In</option>
        <option value="take out">Take Out</option>
      </select>
      <input className="order-field-inp order-tavolo-inp" placeholder="Tavolo" value={editFields.numero_tavolo || ''} onChange={e => setEFv('numero_tavolo', e.target.value)} />
      <input className="order-field-inp" placeholder="Starter" value={editFields.starter || ''} onChange={e => setEFv('starter', e.target.value)} />
      <input className="order-field-inp" placeholder="Main" value={editFields.main || ''} onChange={e => setEFv('main', e.target.value)} />
      <input className="order-field-inp" placeholder="Dessert" value={editFields.dessert || ''} onChange={e => setEFv('dessert', e.target.value)} />
      <input className="order-field-inp order-note-inp" placeholder="Note" value={editFields.note || ''} onChange={e => setEFv('note', e.target.value)} />
      <div className="order-edit-btns">
        <button className="btn-save" onClick={saveEdit}>✓</button>
        <button className="btn-cancel" onClick={() => setEditId(null)}>✕</button>
      </div>
    </div>
  ) : (
    <div className={`order-row ${o.status === 'active' ? 'order-active' : 'order-completed'}`} key={o.id}>
      <div className="order-time">{fmtOrderTime(o.created_at)}</div>
      <div className={`order-status-badge ${o.status === 'active' ? 'order-badge-active' : 'order-badge-done'}`}>
        {o.status === 'active' ? 'ATTIVO' : 'CONCLUSO'}
      </div>
      <div className="order-tipo">{o.tipo || '—'}</div>
      <div className="order-tavolo">{o.numero_tavolo ? `T.${o.numero_tavolo}` : '—'}</div>
      <div className="order-food">
        {o.starter && <span className="order-food-tag order-starter" title="Starter">S: {o.starter}</span>}
        {o.main    && <span className="order-food-tag order-main"    title="Main">M: {o.main}</span>}
        {o.dessert && <span className="order-food-tag order-dessert" title="Dessert">D: {o.dessert}</span>}
        {!o.starter && !o.main && !o.dessert && <span className="order-food-empty">—</span>}
      </div>
      {o.note && <div className="order-note-inline">{o.note}</div>}
      <div className="order-actions">
        {o.status === 'active' && (
          <span className="order-complete-btn" title="Concludi ordine" onClick={() => handleComplete(o)}>✓</span>
        )}
        <span className="order-edit-btn" title="Modifica" onClick={() => startEdit(o)}>✎</span>
        <span className="order-del-btn" title="Elimina" onClick={() => handleDelete(o)}>✕</span>
      </div>
    </div>
  );

  return (
    <div className="panel">
      <div className="panel-hdr">
        <span>// ORDINI {active.length > 0 && <span className="order-hdr-count">{active.length} ATTIVI</span>}</span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span className="btn-add-event" onClick={() => { setShow(v => !v); setEditId(null); }}>
            {showForm ? '✕' : '+ ORDINE'}
          </span>
          <span className="pclose" onClick={onClose}>[X]</span>
        </div>
      </div>

      {adminErr && <div className="order-admin-err">{adminErr}</div>}

      {showForm && (
        <div className="ev-form">
          <div className="form-label">NUOVO ORDINE</div>
          <div className="form-grid">
            <div className="form-field">
              <span className="field-lbl">TIPO</span>
              <select className="field-inp order-tipo-sel" value={form.tipo} onChange={e => setF('tipo', e.target.value)}>
                <option value="">—</option>
                <option value="eat in">Eat In</option>
                <option value="take out">Take Out</option>
              </select>
            </div>
            <div className="form-field">
              <span className="field-lbl">TAVOLO</span>
              <input className="field-inp" placeholder="n. tavolo" value={form.numero_tavolo}
                onChange={e => setF('numero_tavolo', e.target.value)} />
            </div>
            <div className="form-field form-wide">
              <span className="field-lbl">STARTER</span>
              <input className="field-inp" placeholder="opzionale" value={form.starter}
                onChange={e => setF('starter', e.target.value)} />
            </div>
            <div className="form-field form-wide">
              <span className="field-lbl">MAIN</span>
              <input className="field-inp" placeholder="opzionale" value={form.main}
                onChange={e => setF('main', e.target.value)} onKeyDown={e => e.key === 'Enter' && submit()} />
            </div>
            <div className="form-field form-wide">
              <span className="field-lbl">DESSERT</span>
              <input className="field-inp" placeholder="opzionale" value={form.dessert}
                onChange={e => setF('dessert', e.target.value)} />
            </div>
            <div className="form-field form-wide">
              <span className="field-lbl">NOTE</span>
              <input className="field-inp" placeholder="opzionale" value={form.note}
                onChange={e => setF('note', e.target.value)} />
            </div>
          </div>
          <button className="form-submit" onClick={submit}>SALVA ORDINE</button>
        </div>
      )}

      {active.length === 0 && completed.length === 0 && !showForm && (
        <div className="p-empty">nessun ordine — premi + ORDINE o usa la voce</div>
      )}

      {active.length > 0 && (
        <>
          <div className="order-section-label order-section-active">ORDINI ATTIVI</div>
          <div className="order-table-header">
            <span className="oth-time">Orario</span>
            <span className="oth-status">Stato</span>
            <span className="oth-tipo">Tipo</span>
            <span className="oth-tavolo">Tav.</span>
            <span className="oth-food">Portate</span>
            <span className="oth-actions"></span>
          </div>
          {active.map(o => <OrderRow key={o.id} o={o} />)}
        </>
      )}

      {completed.length > 0 && (
        <>
          <div className="order-section-label order-section-done">ORDINI CONCLUSI</div>
          <div className="order-table-header">
            <span className="oth-time">Orario</span>
            <span className="oth-status">Stato</span>
            <span className="oth-tipo">Tipo</span>
            <span className="oth-tavolo">Tav.</span>
            <span className="oth-food">Portate</span>
            <span className="oth-actions"></span>
          </div>
          {completed.map(o => <OrderRow key={o.id} o={o} />)}
        </>
      )}
    </div>
  );
}

// ── Notification ──────────────────────────────────────────────────────────────
export function Notification({ events }) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;
  const now      = new Date();
  const todayStr = now.toISOString().split('T')[0];
  const soon = events?.find(ev => {
    if (!ev.date || ev.date !== todayStr || !ev.time) return false;
    const evMs = new Date(ev.date+'T'+ev.time).getTime();
    const diff = (evMs - now.getTime()) / 60000;
    return diff > 0 && diff <= 30;
  });
  if (!soon) return null;
  const mins = Math.round((new Date(soon.date+'T'+soon.time).getTime()-now.getTime())/60000);
  return (
    <div className="notif">
      <span className="notif-ic">⚡</span>
      <div>
        <div className="notif-t">{soon.title}</div>
        <div className="notif-s">{t('notification.in_minutes', { mins })}</div>
      </div>
      <span className="notif-x" onClick={()=>setDismissed(true)}>{t('common.close')}</span>
    </div>
  );
}
